/**
 * FlexDate primitive — a precision-aware date/time value.
 *
 * Supports five valid shapes (via JSDoc; this is a JS codebase):
 *
 *   1. year-only      { year }
 *   2. year-month     { year, month }
 *   3. date-only      { year, month, day }
 *   4. datetime       { year, month, day, hour, minute, tz? }
 *   5. annual         { year: null, month, day }   (recurring birthday/anniversary)
 *
 * Precision is the most-specific level present:
 *   'year' | 'month' | 'day' | 'minute' | 'annual'
 *
 * Calendar conventions:
 *   - Months are 1..12, days are 1..(days in month), hours 0..23, minutes 0..59.
 *   - tz is an IANA name string (e.g. 'America/New_York'); optional on datetime.
 *   - Feb 29 in non-leap years renders/projects to Feb 28 (Google Calendar rule).
 *
 * All functions in this module are pure: no I/O, no mutation of inputs.
 *
 * @typedef {Object} FlexDate
 * @property {number|null} [year]   Integer year, or null for annual recurrence.
 * @property {number}      [month]  1..12.
 * @property {number}      [day]    1..31 (clamped to month length).
 * @property {number}      [hour]   0..23.
 * @property {number}      [minute] 0..59.
 * @property {string}      [tz]     IANA timezone name (datetime only).
 *
 * @typedef {'year'|'month'|'day'|'minute'|'annual'} Precision
 *
 * @typedef {'month'|'week'|'day'|'agenda'} CalendarLayout
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function isInt(v) {
  return typeof v === 'number' && Number.isInteger(v);
}

function isLeap(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year, month) {
  if (month === 2) {
    return year == null || isLeap(year) ? 29 : 28;
  }
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

function pad2(n) {
  return n < 10 ? `0${n}` : `${n}`;
}

function toJDN(year, month, day) {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return day
    + Math.floor((153 * m + 2) / 5)
    + 365 * y
    + Math.floor(y / 4)
    - Math.floor(y / 100)
    + Math.floor(y / 400)
    - 32045;
}

function fromJDN(jdn) {
  const a = jdn + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  const day = e - Math.floor((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * Math.floor(m / 10);
  const year = 100 * b + d - 4800 + Math.floor(m / 10);
  return { year, month, day };
}

/**
 * Return true iff `fd` is a well-formed FlexDate matching one of the five shapes.
 * Rejects gappy shapes (e.g. {year, day} without month, {year, month, day, hour}
 * without minute, etc.).
 *
 * @param {*} fd
 * @returns {boolean}
 */
export function isValid(fd) {
  if (fd == null || typeof fd !== 'object') return false;

  const hasYear   = 'year'   in fd && fd.year !== undefined;
  const hasMonth  = 'month'  in fd && fd.month !== undefined;
  const hasDay    = 'day'    in fd && fd.day !== undefined;
  const hasHour   = 'hour'   in fd && fd.hour !== undefined;
  const hasMinute = 'minute' in fd && fd.minute !== undefined;
  const hasTz     = 'tz'     in fd && fd.tz !== undefined;

  if (hasYear && fd.year !== null && !isInt(fd.year)) return false;
  if (hasMonth  && (!isInt(fd.month)  || fd.month  < 1 || fd.month  > 12)) return false;
  if (hasHour   && (!isInt(fd.hour)   || fd.hour   < 0 || fd.hour   > 23)) return false;
  if (hasMinute && (!isInt(fd.minute) || fd.minute < 0 || fd.minute > 59)) return false;
  if (hasTz && typeof fd.tz !== 'string') return false;

  if (hasDay) {
    if (!isInt(fd.day) || fd.day < 1 || fd.day > 31) return false;
    if (!hasMonth) return false;
    const max = daysInMonth(hasYear ? fd.year : null, fd.month);
    if (fd.day > max) return false;
  }

  if (hasHour !== hasMinute) return false;
  if ((hasHour || hasMinute) && !hasDay) return false;
  if (hasTz && !hasHour) return false;

  if (fd.year === null) {
    return hasMonth && hasDay && !hasHour;
  }

  if (!hasYear) return false;

  if (!hasMonth)  return !hasDay && !hasHour;
  if (!hasDay)    return !hasHour;
  return true;
}

/**
 * Return the precision tier of `fd`. Returns null if invalid.
 *
 * @param {FlexDate} fd
 * @returns {Precision|null}
 */
export function precision(fd) {
  if (!isValid(fd)) return null;
  if (fd.year === null) return 'annual';
  if (fd.hour !== undefined) return 'minute';
  if (fd.day !== undefined) return 'day';
  if (fd.month !== undefined) return 'month';
  return 'year';
}

/**
 * True iff `fd` is the recurring annual shape (year === null).
 *
 * @param {FlexDate} fd
 * @returns {boolean}
 */
export function isAnnual(fd) {
  return precision(fd) === 'annual';
}

/**
 * Build a date-only or datetime FlexDate from a JS Date (using its local fields).
 * If `withTime` is true, also captures hour/minute (precision = 'minute').
 *
 * @param {Date} d
 * @param {{ withTime?: boolean, tz?: string }} [opts]
 * @returns {FlexDate}
 */
export function fromDate(d, opts = {}) {
  if (!(d instanceof Date) || isNaN(d.getTime())) {
    throw new TypeError('fromDate: expected a valid Date');
  }
  const { withTime = false, tz } = opts;
  const fd = {
    year:  d.getFullYear(),
    month: d.getMonth() + 1,
    day:   d.getDate(),
  };
  if (withTime) {
    fd.hour   = d.getHours();
    fd.minute = d.getMinutes();
    if (tz) fd.tz = tz;
  }
  return fd;
}

/**
 * Convert a FlexDate to a concrete JS Date in local time.
 * For low-precision shapes the missing fields default to the start of the unit:
 *   - year-only  → Jan 1 of that year
 *   - year-month → 1st of that month
 *   - date-only  → midnight local
 * For annuals, returns null — caller must `projectToYear` first.
 * Feb 29 of a non-leap year is clamped to Feb 28.
 *
 * @param {FlexDate} fd
 * @returns {Date|null}
 */
export function toDate(fd) {
  if (!isValid(fd)) return null;
  if (fd.year === null) return null;
  const year   = fd.year;
  const month  = fd.month  ?? 1;
  let   day    = fd.day    ?? 1;
  const hour   = fd.hour   ?? 0;
  const minute = fd.minute ?? 0;
  const max = daysInMonth(year, month);
  if (day > max) day = max;
  return new Date(year, month - 1, day, hour, minute, 0, 0);
}

/**
 * Format a FlexDate for display. Shape-aware:
 *   year-only   → "2026"
 *   year-month  → "April 2026"
 *   date-only   → "April 27, 2026"
 *   datetime    → "April 27, 2026 2:30 PM" (24h if opts.hour12 === false)
 *   annual      → "April 15"
 *
 * @param {FlexDate} fd
 * @param {{ short?: boolean, hour12?: boolean }} [opts]
 * @returns {string}
 */
export function format(fd, opts = {}) {
  if (!isValid(fd)) return '';
  const { short = false, hour12 = true } = opts;
  const months = short ? MONTH_ABBR : MONTH_NAMES;
  const p = precision(fd);

  if (p === 'year')   return `${fd.year}`;
  if (p === 'month')  return `${months[fd.month - 1]} ${fd.year}`;
  if (p === 'annual') return `${months[fd.month - 1]} ${fd.day}`;

  const datePart = `${months[fd.month - 1]} ${fd.day}, ${fd.year}`;
  if (p === 'day') return datePart;

  let timePart;
  if (hour12) {
    const h = fd.hour % 12 || 12;
    const ampm = fd.hour < 12 ? 'AM' : 'PM';
    timePart = `${h}:${pad2(fd.minute)} ${ampm}`;
  } else {
    timePart = `${pad2(fd.hour)}:${pad2(fd.minute)}`;
  }
  return `${datePart} ${timePart}`;
}

function toInstant(fd, anchor) {
  if (!isValid(fd)) return null;
  let resolved = fd;
  if (fd.year === null) {
    if (!anchor || !isInt(anchor.year)) return null;
    resolved = projectToYear(fd, anchor.year);
  }
  const d = toDate(resolved);
  return d ? d.getTime() : null;
}

/**
 * Compare two FlexDates. Returns -1 / 0 / 1, or NaN on incomparable inputs.
 * Annuals require an `anchor` year for mixed comparisons; without one, two
 * annuals still compare by month/day.
 *
 * @param {FlexDate} a
 * @param {FlexDate} b
 * @param {{ anchor?: { year: number } }} [opts]
 * @returns {number}
 */
export function compare(a, b, opts = {}) {
  if (!isValid(a) || !isValid(b)) return NaN;
  const aAnnual = a.year === null;
  const bAnnual = b.year === null;

  if (aAnnual && bAnnual) {
    if (a.month !== b.month) return a.month < b.month ? -1 : 1;
    if (a.day   !== b.day)   return a.day   < b.day   ? -1 : 1;
    return 0;
  }

  const anchor = opts.anchor;
  if ((aAnnual || bAnnual) && !anchor) return NaN;

  const ta = toInstant(a, anchor);
  const tb = toInstant(b, anchor);
  if (ta == null || tb == null) return NaN;
  if (ta < tb) return -1;
  if (ta > tb) return 1;
  return 0;
}

/**
 * True iff `fd` falls within [start, end] inclusive.
 * Annuals are projected onto each year that overlaps the range and tested.
 *
 * @param {FlexDate} fd
 * @param {FlexDate} start
 * @param {FlexDate} end
 * @returns {boolean}
 */
export function inRange(fd, start, end) {
  if (!isValid(fd) || !isValid(start) || !isValid(end)) return false;
  if (start.year === null || end.year === null) return false;

  if (fd.year === null) {
    for (let y = start.year; y <= end.year; y++) {
      const projected = projectToYear(fd, y);
      const c1 = compare(projected, start);
      const c2 = compare(projected, end);
      if (c1 >= 0 && c2 <= 0) return true;
    }
    return false;
  }

  return compare(fd, start) >= 0 && compare(fd, end) <= 0;
}

/**
 * Project an annual FlexDate onto a specific year, returning a date-only FlexDate.
 * Feb 29 is clamped to Feb 28 in non-leap years (Google Calendar convention).
 * If `fd` is not an annual, it is returned unchanged.
 *
 * @param {FlexDate} fd
 * @param {number}   year
 * @returns {FlexDate}
 */
export function projectToYear(fd, year) {
  if (!isValid(fd)) return fd;
  if (fd.year !== null) return fd;
  if (!isInt(year)) throw new TypeError('projectToYear: year must be an integer');
  let day = fd.day;
  if (fd.month === 2 && day === 29 && !isLeap(year)) {
    day = 28;
  }
  return { year, month: fd.month, day };
}

/**
 * Shift a FlexDate by an integer number of days, preserving precision.
 *   - year-only:  shifts year by Math.trunc(deltaDays / 365). Year-only is
 *                 non-draggable per `isDraggable`, so this is mainly a fallback.
 *   - year-month: shifts by whole months (deltaDays / 30, rounded toward zero).
 *   - date-only / datetime: true calendar-day shift; datetime keeps time + tz.
 *   - annual:     month/day shift via projection through 2024 (a leap year);
 *                 year stays null.
 *
 * @param {FlexDate} fd
 * @param {number}   deltaDays
 * @returns {FlexDate|null}
 */
export function shiftBy(fd, deltaDays) {
  if (!isValid(fd) || !isInt(deltaDays)) return null;
  const p = precision(fd);

  if (p === 'year') {
    return { year: fd.year + Math.trunc(deltaDays / 365) };
  }

  if (p === 'month') {
    const months = Math.trunc(deltaDays / 30);
    let m = fd.month - 1 + months;
    const y = fd.year + Math.floor(m / 12);
    m = ((m % 12) + 12) % 12;
    return { year: y, month: m + 1 };
  }

  if (p === 'annual') {
    const ref = projectToYear(fd, 2024);
    const jdn = toJDN(ref.year, ref.month, ref.day) + deltaDays;
    const { month, day } = fromJDN(jdn);
    return { year: null, month, day };
  }

  const jdn = toJDN(fd.year, fd.month, fd.day) + deltaDays;
  const { year, month, day } = fromJDN(jdn);
  const out = { year, month, day };
  if (p === 'minute') {
    out.hour   = fd.hour;
    out.minute = fd.minute;
    if (fd.tz) out.tz = fd.tz;
  }
  return out;
}

/**
 * Shift a FlexDate by `n` units, where unit is 'minutes' | 'hours' | 'days'.
 *
 * Validation policy (returns null on mismatch rather than throwing, so DnD
 * handlers can fall through without try/catch):
 *   - 'minutes' and 'hours' require a datetime (precision === 'minute').
 *   - 'days' works on date-only, datetime, and annual.
 *   - For year-only / year-month, every unit returns null — those tiers are
 *     non-draggable per `isDraggable` and have no day affordance.
 *
 * @param {FlexDate} fd
 * @param {number}   n
 * @param {'minutes'|'hours'|'days'} unit
 * @returns {FlexDate|null}
 */
export function shiftByUnits(fd, n, unit) {
  if (!isValid(fd) || !isInt(n)) return null;
  const p = precision(fd);

  if (unit === 'minutes' || unit === 'hours') {
    if (p !== 'minute') return null;
    const totalMinutes = unit === 'hours' ? n * 60 : n;
    const base = toDate(fd);
    if (!base) return null;
    base.setMinutes(base.getMinutes() + totalMinutes);
    const out = {
      year:   base.getFullYear(),
      month:  base.getMonth() + 1,
      day:    base.getDate(),
      hour:   base.getHours(),
      minute: base.getMinutes(),
    };
    if (fd.tz) out.tz = fd.tz;
    return out;
  }

  if (unit === 'days') {
    if (p !== 'day' && p !== 'minute' && p !== 'annual') return null;
    return shiftBy(fd, n);
  }

  return null;
}

/**
 * Whether `fd` is draggable in the given calendar layout.
 *
 * Per the Calendar View plan:
 *                 month   week   day   list
 *   year-only     no      no     no    no
 *   year-month    no      no     no    no
 *   date-only     yes     yes    yes   yes
 *   datetime      yes     yes    yes   yes
 *   annual        yes     yes    yes   yes
 *
 * @param {FlexDate} fd
 * @param {CalendarLayout} layout
 * @returns {boolean}
 */
export function isDraggable(fd, layout) {
  if (!isValid(fd)) return false;
  const p = precision(fd);
  if (p === 'year' || p === 'month') return false;
  const draggableLayouts = new Set(['month', 'week', 'day', 'nDay', 'agenda', 'track']);
  if (!draggableLayouts.has(layout)) return false;
  return true;
}
